<script setup lang="ts">
import FinestButton from '@/components/FinestButton.vue'
import { portfolioTabForField } from '@/pages/investors/portfolio/tabs'
import { computed } from 'vue'
import { useI18n } from 'vue-i18n'
import SubmissionGapEntityData = App.Data.Project.SubmissionGapEntityData
import SubmissionGapItemData = App.Data.Project.SubmissionGapItemData
import SubmissionGapReportData = App.Data.Project.SubmissionGapReportData

const props = defineProps<{
  report: SubmissionGapReportData
}>()

const emit = defineEmits<{
  complete: [fieldPath: string | null]
}>()

const { t } = useI18n()

interface GapRow {
  key: string
  group: string
  label: string
  href: string | null
  isOwnField: boolean
  fieldPath: string | null
}

const editUrl = (entity: SubmissionGapEntityData, item: SubmissionGapItemData): string | null => {
  if (entity.edit_url === null || entity.entity_type !== 'estate-object') {
    return entity.edit_url
  }

  const separator = entity.edit_url.includes('?') ? '&' : '?'

  return `${entity.edit_url}${separator}tab=${portfolioTabForField(item.field_path)}`
}

const toRow = (entity: SubmissionGapEntityData, item: SubmissionGapItemData): GapRow => {
  const isOwnField = entity.entity_type === 'project'

  return {
    key: `${entity.entity_type}-${entity.entity_id}-${item.code}`,
    group: t(`groups.${entity.entity_type}`),
    label: item.label,
    href: isOwnField ? null : editUrl(entity, item),
    isOwnField,
    fieldPath: item.field_path,
  }
}

const entities = computed<SubmissionGapEntityData[]>(() =>
  [props.report.project, props.report.estate_objects, props.report.companies, props.report.natural_persons].flat(),
)

const rows = computed<GapRow[]>(() =>
  entities.value.flatMap((entity) => entity.missing_items.map((item) => toRow(entity, item))),
)

const has = (entityType: string): boolean => entities.value.some((entity) => entity.entity_type === entityType)

/** Whose data the bank is waiting for decides the wording — the buyer comes before the property. */
const subject = computed(() => {
  if (has('natural-person') || has('credit')) {
    return 'person'
  }

  if (has('company')) {
    return 'company'
  }

  return has('estate-object') ? 'object' : 'request'
})

const areaLinks = computed(() =>
  entities.value
    .filter((entity) => entity.entity_type !== 'project' && entity.edit_url !== null)
    .map((entity) => ({ href: entity.edit_url as string, label: t(`areas.${entity.entity_type}`) })),
)
</script>

<template>
  <section v-if="rows.length" class="border-warning-subtle-active bg-warning-subtle border-t px-5 py-5">
    <div class="mx-auto max-w-3xl">
      <div class="flex items-start gap-3.5">
        <span
          class="bg-warning flex size-9 flex-none items-center justify-center gap-[3px] rounded-[10px]"
          aria-hidden="true">
          <i class="block h-3.5 w-1 rounded-sm bg-white" />
          <i class="block h-3.5 w-1 rounded-sm bg-white" />
        </span>

        <div class="min-w-0">
          <p class="text-warning text-[10px] font-extrabold tracking-[0.14em] uppercase">{{ t('eyebrow') }}</p>

          <div class="mt-0.5 flex flex-wrap items-baseline gap-x-3 gap-y-1">
            <h3 class="text-warning text-[17px] leading-snug font-extrabold tracking-tight">
              {{ t(`headline.${subject}`) }}
            </h3>
            <span
              class="border-warning-subtle-active text-warning bg-surface rounded-full border px-2.5 py-0.5 text-[11px] font-bold">
              {{ t('open', { count: rows.length }, rows.length) }}
            </span>
          </div>

          <i18n-t v-if="areaLinks.length" keypath="explanation.withAreas" tag="p" class="text-ink-2 mt-1.5 text-sm">
            <template #areas>
              <template v-for="(area, index) in areaLinks" :key="area.href">
                <span v-if="index > 0">{{ index === areaLinks.length - 1 ? t('and') : ', ' }}</span>
                <Link :href="area.href" class="text-action font-semibold hover:underline">{{ area.label }}</Link>
              </template>
            </template>
          </i18n-t>

          <p v-else class="text-ink-2 mt-1.5 text-sm">{{ t('explanation.ownFields') }}</p>
        </div>
      </div>

      <ul class="border-warning-subtle-active bg-surface mt-4 overflow-hidden rounded-[10px] border">
        <li
          v-for="row in rows"
          :key="row.key"
          class="border-line-2 flex flex-wrap items-center gap-x-4 gap-y-2 border-b px-3.5 py-2.5 last:border-b-0">
          <span class="text-warning w-24 flex-none text-[10px] font-extrabold tracking-[0.1em] uppercase">
            {{ row.group }}
          </span>

          <span class="text-ink min-w-0 flex-1 text-sm font-semibold">{{ row.label }}</span>

          <Link v-if="row.href" :href="row.href" class="flex-none">
            <FinestButton
              variant="secondary"
              size="sm"
              icon="pi pi-arrow-right"
              icon-pos="right"
              :label="t('complete')" />
          </Link>

          <FinestButton
            v-else-if="row.isOwnField"
            class="flex-none"
            variant="secondary"
            size="sm"
            icon="pi pi-arrow-down"
            icon-pos="right"
            :label="t('complete')"
            @click="emit('complete', row.fieldPath)" />
        </li>
      </ul>
    </div>
  </section>
</template>

<i18n lang="json">
{
  "de": {
    "eyebrow": "Wir warten auf dich",
    "headline": {
      "person": "Die Bank braucht vollständige Daten über deine Person",
      "company": "Die Bank braucht vollständige Daten über den Käufer",
      "object": "Die Bank braucht vollständige Daten zum Objekt",
      "request": "Die Bank braucht noch Angaben zu deiner Finanzierung"
    },
    "explanation": {
      "withAreas": "Solange sie fehlen, kann die Anfrage nicht weiter geprüft werden. Ergänze sie in {areas} — danach läuft es automatisch weiter.",
      "ownFields": "Solange sie fehlen, kann die Anfrage nicht weiter geprüft werden. Ergänze sie unten in den Eckdaten — danach läuft es automatisch weiter."
    },
    "and": " und ",
    "open": "{count} Angaben offen | {count} Angabe offen | {count} Angaben offen",
    "areas": {
      "estate-object": "den Objektdaten",
      "company": "den Unternehmensdaten",
      "natural-person": "deinem Profil",
      "credit": "deiner Bonität"
    },
    "complete": "Ergänzen",
    "groups": {
      "project": "Anfrage",
      "estate-object": "Objekt",
      "company": "Unternehmen",
      "natural-person": "Profil",
      "credit": "Bonität"
    }
  },
  "en": {
    "eyebrow": "We are waiting for you",
    "headline": {
      "person": "The bank needs complete data about you",
      "company": "The bank needs complete data about the buyer",
      "object": "The bank needs complete data about the property",
      "request": "The bank still needs details about your financing"
    },
    "explanation": {
      "withAreas": "As long as they are missing, the request cannot be reviewed any further. Complete them in {areas} — after that it continues automatically.",
      "ownFields": "As long as they are missing, the request cannot be reviewed any further. Complete them in the key facts below — after that it continues automatically."
    },
    "and": " and ",
    "open": "{count} details open | {count} detail open | {count} details open",
    "areas": {
      "estate-object": "the property data",
      "company": "the company data",
      "natural-person": "your profile",
      "credit": "your credit details"
    },
    "complete": "Complete",
    "groups": {
      "project": "Request",
      "estate-object": "Property",
      "company": "Company",
      "natural-person": "Profile",
      "credit": "Credit"
    }
  }
}
</i18n>
